/**
 * O estado que o cabeçalho do agente mostra, quando existe trabalho de fora da
 * execução dele (#853).
 *
 * O Architect tem duas entidades que andam sozinhas: a `agent_executions`, que
 * é a run do grafo, e o `wricef_scan_job`, que é a varredura da base e dura
 * horas. O cabeçalho lia só a primeira, e com a varredura extraindo a tela
 * dizia `COMPLETED` · `Current Ready` · "Last execution completed successfully"
 * ao lado de uma barra andando e de um botão "Pause".
 *
 * A regra: a execução manda enquanto tiver algo a dizer — rodando, pausada ou
 * falha são estados dela que ninguém pode esconder. Só quando ela está quieta é
 * que o trabalho de fora assume o cabeçalho.
 */

export interface ExecutionHeroInput {
  isRunning: boolean;
  isComplete: boolean;
  isLocked: boolean;
  executionDone: boolean;
  status: string;
}

export interface HeroState {
  /** O que sai na overline: `RUNNING`, `COMPLETED`, `PAUSED`, `FAILED`, … */
  runState: string;
  /** O rótulo de "Current" na linha de meta do hero. */
  current: string;
  /** O status do banner logo abaixo do hero. */
  bannerStatus: "In Progress" | "Complete" | "Paused" | "Not Started";
  /** O texto do banner. */
  bannerDetail: string;
  /**
   * Se o tempo decorrido pode sair da execução.
   *
   * Falso quando quem está rodando é o trabalho de fora: o print de 30/09
   * mostrava `Elapsed 0:48` — a latência da execução que já tinha terminado —
   * ao lado de uma varredura estimada em duas horas e meia.
   */
  usesExecutionElapsed: boolean;
  /**
   * Quem decidiu este estado.
   *
   * O componente tem textos mais ricos para a execução (a fase corrente entra
   * no banner, por exemplo) e continua usando os dele nesse caso; só quando o
   * trabalho de fora assume é que ele adota os daqui.
   */
  source: "execution" | "background";
}

export function heroStateFor(
  execution: ExecutionHeroInput,
  backgroundActivity: string | null | undefined,
): HeroState {
  if (execution.isRunning) {
    return {
      runState: "RUNNING",
      current: "processing",
      bannerStatus: "In Progress",
      bannerDetail: "Executing…",
      usesExecutionElapsed: true,
      source: "execution",
    };
  }
  if (execution.status === "paused") {
    return {
      runState: "PAUSED",
      current: "Paused — awaiting input",
      bannerStatus: "Paused",
      bannerDetail: "Paused",
      usesExecutionElapsed: false,
      source: "execution",
    };
  }
  if (execution.status === "failed") {
    return {
      runState: "FAILED",
      current: "Standby",
      bannerStatus: "Not Started",
      bannerDetail: "Ready to run",
      usesExecutionElapsed: false,
      source: "execution",
    };
  }
  if (backgroundActivity) {
    return {
      runState: "RUNNING",
      current: backgroundActivity,
      bannerStatus: "In Progress",
      bannerDetail: backgroundActivity,
      usesExecutionElapsed: false,
      source: "background",
    };
  }
  if (execution.isComplete || execution.executionDone) {
    return {
      runState: "COMPLETED",
      current: "Ready",
      bannerStatus: "Complete",
      bannerDetail: "Last execution completed successfully",
      usesExecutionElapsed: false,
      source: "execution",
    };
  }
  if (execution.isLocked) {
    return {
      runState: "LOCKED",
      current: "Waiting upstream",
      bannerStatus: "Not Started",
      bannerDetail: "Waiting for upstream agents to complete",
      usesExecutionElapsed: false,
      source: "execution",
    };
  }
  return {
    runState: "IDLE",
    current: "Standby",
    bannerStatus: "Not Started",
    bannerDetail: "Ready to run",
    usesExecutionElapsed: false,
    source: "execution",
  };
}
